<script setup lang="ts">
import { ref, useId } from 'vue'
import MarkdownText from '@/components/MarkdownText.vue'
import SlidePanel from '@/components/SlidePanel.vue'
import type { ItemPassage } from './assessment_item_types'

withDefaults(defineProps<{ passage: ItemPassage, isLocked?: boolean }>(), { isLocked: false })

// The opening paragraph stays readable as a teaser; everything after it, every list item and
// every image is blurred, and links blur in their own colour.
const LOCKED_PASSAGE_CLASSES = [
  '[&_:is(div:not(:first-child),p:not(:first-child),p:only-child,li)]:select-none',
  '[&_:is(div:not(:first-child),p:not(:first-child),p:only-child,li)]:text-[transparent]',
  '[&_:is(div:not(:first-child),p:not(:first-child),p:only-child,li)]:[text-shadow:0_0_14px_black]',
  '[&_:is(div:not(:first-child),p:not(:first-child),p:only-child,li)_a]:!text-[transparent]',
  '[&_:is(div:not(:first-child),p:not(:first-child),p:only-child,li)_a]:[text-shadow:0_0_14px_#245682]',
  '[&_:is(div:not(:first-child),p:not(:first-child),p:only-child,li)_a]:no-underline',
  '[&_img]:blur-sm'
].join(' ')

// The passage sits under the modal's h4 "Student Task and Resources".
const PASSAGE_TOP_HEADING_LEVEL = 5

const isPassageShown = ref(true)
const passageBodyId = useId()

function togglePassage() {
  isPassageShown.value = !isPassageShown.value
}
</script>

<template>
  <div class="mb-5 overflow-hidden rounded-xs border border-gray-300 bg-white shadow-sm">
    <button
      type="button"
      class="flex w-full items-center gap-2 rounded-t-xs border-b border-gray-300 bg-gray-100 px-4 py-2.5 text-left text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-gray-900"
      :aria-expanded="isPassageShown"
      :aria-controls="passageBodyId"
      data-testid="item-passage-toggle"
      @click="togglePassage"
    >
      <span class="flex-auto"><b>Passage:</b> {{ passage.passage_title }}</span>
      <span
        class="material-icons-round flex-none transition-transform duration-300 motion-reduce:transition-none"
        :class="{ 'rotate-180': isPassageShown }"
        aria-hidden="true"
      >expand_more</span>
    </button>
    <SlidePanel :expanded="isPassageShown">
      <div :id="passageBodyId" class="p-4" data-testid="item-passage-body">
        <MarkdownText
          :text="passage.passage_text"
          :top-heading-level="PASSAGE_TOP_HEADING_LEVEL"
          class="break-words [font-family:Times,serif]"
          :class="{ [LOCKED_PASSAGE_CLASSES]: isLocked }"
          :inert="isLocked || undefined"
        />
      </div>
    </SlidePanel>
  </div>
</template>
